import { MouseEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import {
  errorApiRef,
  identityApiRef,
  useAnalytics,
  useApi,
} from '@backstage/core-plugin-api';
import { useUserProfile } from '@backstage/plugin-user-settings';
import { accountNameFor, greetingNameFor, initialsFor } from './viewModel';

const SIGN_OUT_ERROR = 'Sign out failed. Check your connection and try again.';

// Anchored top-left: at its static position (after the last item) the 1px box
// overflows the popover and summons a classic-scrollbar track on Windows.
const visuallyHiddenSx = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const;

// Avatar-only account trigger for the hero corner (the greeting already names
// the user). Opens a compact right-aligned menu: identity header, Profile and
// settings, and Sign out.
export const AccountMenu = () => {
  const { displayName, profile, loading } = useUserProfile();
  const identityApi = useApi(identityApiRef);
  const errorApi = useApi(errorApiRef);
  const analytics = useAnalytics();
  const navigate = useNavigate();
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down('sm'));
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [signingOut, setSigningOut] = useState(false);
  const [failed, setFailed] = useState(false);

  if (loading) return null;

  const fullName = greetingNameFor(displayName);
  const name = accountNameFor(displayName);
  const initials = initialsFor(name);
  const email = profile?.email;
  const open = Boolean(anchorEl);
  const dark = theme.palette.mode === 'dark';
  // The dark accent fails AA with white initials, so the avatar flips to ink.
  const avatarInk = dark
    ? theme.palette.background.default
    : theme.palette.common.white;
  const dangerSoft = alpha(theme.palette.error.main, dark ? 0.16 : 0.08);

  const itemSx = {
    mx: 0.75,
    my: 0.75,
    borderRadius: '8px',
    minHeight: compact ? 48 : 44,
    px: 1.25,
    py: 0.75,
    gap: 1.25,
    alignItems: 'center',
    whiteSpace: 'normal' as const,
    '&.Mui-focusVisible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: '-2px',
    },
  };

  const openMenu = (event: MouseEvent<HTMLElement>) => {
    setFailed(false);
    setAnchorEl(event.currentTarget);
  };

  const goToSettings = () => {
    setAnchorEl(null);
    navigate('/settings');
  };

  const signOut = async () => {
    if (signingOut) return;
    setFailed(false);
    setSigningOut(true);
    analytics.captureEvent('signOut', 'account-menu');
    try {
      // On success the identity api ends the session and leaves the page.
      await identityApi.signOut();
    } catch {
      setSigningOut(false);
      setFailed(true);
      analytics.captureEvent('signOutFailed', 'account-menu');
      errorApi.post(new Error(SIGN_OUT_ERROR));
    }
  };

  return (
    <>
      <Tooltip title={fullName ?? ''}>
        <ButtonBase
          onClick={openMenu}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-controls={open ? 'account-menu' : undefined}
          aria-label="Open account menu"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 40,
            minHeight: 40,
            p: 0.5,
            borderRadius: '10px',
            border: '1px solid transparent',
            '&:hover': {
              borderColor: 'divider',
              bgcolor: alpha(theme.palette.background.paper, 0.72),
            },
            ...(open && {
              borderColor: 'divider',
              bgcolor: 'background.paper',
              boxShadow: 1,
            }),
            '&.Mui-focusVisible': {
              outline: `2px solid ${theme.palette.primary.main}`,
              outlineOffset: '2px',
            },
          }}
        >
          <Avatar
            sx={{
              width: 32,
              height: 32,
              borderRadius: '9px',
              bgcolor: 'primary.main',
              color: avatarInk,
              fontSize: 11.5,
              fontWeight: 700,
            }}
          >
            {initials}
          </Avatar>
        </ButtonBase>
      </Tooltip>
      <Menu
        id="account-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={() => {
          if (!signingOut) setAnchorEl(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        transitionDuration={reducedMotion ? 0 : 120}
        disableScrollLock
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              width: compact ? 'min(340px, calc(100vw - 24px))' : 264,
              borderRadius: '13px',
              border: '1px solid',
              borderColor: 'divider',
            },
          },
        }}
        MenuListProps={{
          'aria-label': 'Account',
          autoFocusItem: true,
          sx: { p: 0 },
        }}
      >
        {/* Identity header: plain content, never a menu item. */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 1.75,
            py: 1.25,
            bgcolor: 'background.default',
          }}
        >
          <Avatar
            sx={{
              width: 32,
              height: 32,
              borderRadius: '9px',
              bgcolor: 'primary.main',
              color: avatarInk,
              fontSize: 11.5,
              fontWeight: 700,
            }}
          >
            {initials}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography noWrap sx={{ fontSize: 13.5, fontWeight: 700 }}>
              {name}
            </Typography>
            {email && (
              <Typography
                noWrap
                sx={{ fontSize: 12, color: 'text.secondary' }}
              >
                {email}
              </Typography>
            )}
          </Box>
        </Box>
        <Divider />
        <MenuItem onClick={goToSettings} sx={itemSx}>
          <SettingsOutlinedIcon
            sx={{ fontSize: 18, color: 'text.secondary', flex: 'none' }}
          />
          <Typography
            sx={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 650 }}
          >
            Profile and settings
          </Typography>
        </MenuItem>
        <Divider sx={{ mx: 1.5 }} />
        <MenuItem
          onClick={signOut}
          aria-disabled={signingOut}
          aria-busy={signingOut}
          sx={{
            ...itemSx,
            '&:hover': { bgcolor: dangerSoft },
            '&.Mui-focusVisible': {
              ...itemSx['&.Mui-focusVisible'],
              bgcolor: dangerSoft,
            },
            ...(signingOut && { opacity: 0.65 }),
          }}
        >
          <LogoutOutlinedIcon
            sx={{ fontSize: 18, color: 'error.main', flex: 'none' }}
          />
          <Typography
            sx={{
              flex: 1,
              minWidth: 0,
              fontSize: 13.5,
              fontWeight: 650,
              color: signingOut ? 'text.primary' : 'error.main',
            }}
          >
            {signingOut ? 'Signing out…' : 'Sign out'}
          </Typography>
          {signingOut && (
            <CircularProgress
              size={16}
              sx={{ color: 'error.main', flex: 'none' }}
            />
          )}
        </MenuItem>
        {failed && (
          <Typography
            role="alert"
            sx={{
              mx: 1.5,
              mb: 1.25,
              px: 1.25,
              py: 1,
              borderRadius: '8px',
              bgcolor: dangerSoft,
              border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
              color: 'error.main',
              fontSize: 12,
              lineHeight: 1.4,
            }}
          >
            {SIGN_OUT_ERROR}
          </Typography>
        )}
        <Box role="status" aria-live="polite" sx={visuallyHiddenSx}>
          {signingOut ? 'Signing out' : ''}
        </Box>
      </Menu>
    </>
  );
};
